import { useEffect, useState } from "react";
import {
  Shield, ShieldCheck, ShieldAlert, KeyRound, Activity,
  Trash2, Clock4, Fingerprint, ChevronRight, Loader2, Copy,
  AlertTriangle, X, BadgeCheck, Globe,
} from "lucide-react";
import TrustCenter from "@/components/settings/TrustCenter";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/* ============================================================
   Settings — Security
   Delta-style card grid (7 cards) plus drawer/modal flows.
   ============================================================ */
export default function SettingsPage() {
  const { user, fetchMe } = useAuth();
  const [drawer, setDrawer] = useState(null); // "2fa" | "activity" | "password" | "delete" | "anti_phishing" | "trust"
  const [busy, setBusy] = useState(false);
  const [phishCode, setPhishCode] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/api/auth/anti-phishing-code")
      .then((r) => setPhishCode(r.data?.anti_phishing_code || ""))
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line

  const toggleSafeSession = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/api/auth/safe-session", { enabled: !user?.safe_session });
      await fetchMe();
    } finally { setBusy(false); }
  };

  const kycMeta = (() => {
    const s = user?.kyc_status || "not_started";
    if (s === "approved") return { label: "Verified",  tone: "ok",   detail: "Identity verified" };
    if (s === "pending_review") return { label: "Under Review", tone: "warn", detail: "Documents are being reviewed" };
    if (s === "rejected") return { label: "Rejected",  tone: "err",  detail: "Re-submit your documents" };
    return { label: "Not Verified", tone: "warn", detail: "Verify to unlock deposits & trading" };
  })();

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-[26px] font-semibold text-foreground tracking-tight">Security</h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            Strengthen and manage how you sign in and keep your account safe.
          </p>
        </div>

        {/* SECURITY HEALTH BANNER */}
        <SecurityScore user={user} phishCode={phishCode} />

        {/* CARD GRID — 7 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Tile
            icon={user?.totp_enabled ? ShieldCheck : Shield}
            title="2FA (Setup)"
            badge={user?.totp_enabled ? "Enabled" : "Disabled"}
            tone={user?.totp_enabled ? "ok" : "warn"}
            description={user?.totp_enabled
              ? "Authenticator app required at every login."
              : "Add an extra step at sign-in with an authenticator."}
            cta={user?.totp_enabled ? "Manage" : "Setup now"}
            onClick={() => setDrawer("2fa")}
            testid="settings-card-2fa"
          />

          <Tile
            icon={Activity}
            title="Account Activity"
            description="Recent logins and active sessions on your account."
            cta="View activity"
            onClick={() => setDrawer("activity")}
            testid="settings-card-activity"
          />

          <Tile
            icon={KeyRound}
            title="Manage Password"
            description="Change the password used to sign in."
            cta="Change password"
            onClick={() => setDrawer("password")}
            testid="settings-card-password"
          />

          <Tile
            icon={Trash2}
            title="Delete Account"
            badge={user?.status === "pending_deletion" ? "Pending" : null}
            tone={user?.status === "pending_deletion" ? "err" : "neutral"}
            description={user?.status === "pending_deletion"
              ? "Account will be deleted soon. Click to cancel."
              : "Permanently remove your PaisaCX account."}
            cta={user?.status === "pending_deletion" ? "Cancel deletion" : "Request deletion"}
            danger={user?.status !== "pending_deletion"}
            onClick={() => setDrawer("delete")}
            testid="settings-card-delete"
          />

          <Tile
            icon={Clock4}
            title="Safe Session"
            badge={user?.safe_session ? "On" : "Off"}
            tone={user?.safe_session ? "ok" : "neutral"}
            description="Auto-logout after 10 minutes of inactivity."
            cta={user?.safe_session ? "Disable" : "Enable"}
            onClick={toggleSafeSession}
            testid="settings-card-safe-session"
          />

          <Tile
            icon={Globe}
            title="Trusted Access"
            badge={user?.can_trade ? "Trading OK" : "Locked"}
            tone={user?.can_trade ? "ok" : "warn"}
            description="Unlock trading via KYC, wallet, IP, or device trust."
            cta="Manage trust"
            onClick={() => setDrawer("trust")}
            testid="settings-card-trust"
          />

          <Tile
            icon={BadgeCheck}
            title="KYC Verification"
            badge={kycMeta.label}
            tone={kycMeta.tone}
            description={kycMeta.detail}
            cta={user?.kyc_status === "approved" ? "View" : "Verify"}
            href="/profile/kyc"
            testid="settings-card-kyc"
          />

          <Tile
            icon={Fingerprint}
            title="Anti-Phishing Code"
            badge={phishCode ? "Set" : "Not set"}
            tone={phishCode ? "ok" : "warn"}
            description={phishCode
              ? `Your code "${phishCode}" appears in every official email.`
              : "Add a personal code shown in every email we send you."}
            cta={phishCode ? "Edit" : "Set code"}
            onClick={() => setDrawer("anti_phishing")}
            testid="settings-card-anti-phishing"
          />
        </div>
      </div>

      {/* Drawers */}
      {drawer === "2fa" && (
        <Drawer title="Two-Factor Authentication" onClose={() => setDrawer(null)}>
          <TwoFactorPanel user={user} onChanged={fetchMe} />
        </Drawer>
      )}
      {drawer === "activity" && (
        <Drawer title="Account Activity" onClose={() => setDrawer(null)} wide>
          <AccountActivityPanel />
        </Drawer>
      )}
      {drawer === "password" && (
        <Drawer title="Change Password" onClose={() => setDrawer(null)}>
          <PasswordPanel onSuccess={() => setTimeout(() => (window.location.href = "/login"), 1500)} />
        </Drawer>
      )}
      {drawer === "delete" && (
        <Drawer title="Delete Account" onClose={() => setDrawer(null)}>
          <DeleteAccountPanel user={user} onChanged={fetchMe} onClose={() => setDrawer(null)} />
        </Drawer>
      )}
      {drawer === "anti_phishing" && (
        <Drawer title="Anti-Phishing Code" onClose={() => setDrawer(null)}>
          <AntiPhishingPanel current={phishCode} onSaved={(v) => { setPhishCode(v); setDrawer(null); }} />
        </Drawer>
      )}
      {drawer === "trust" && (
        <Drawer title="Trusted Access" onClose={() => { setDrawer(null); fetchMe(); }} wide>
          <TrustCenter onClose={() => { setDrawer(null); fetchMe(); }} />
        </Drawer>
      )}
    </AppShell>
  );
}

/* ============================================================ */

function SecurityScore({ user, phishCode }) {
  const checks = [
    { ok: user?.email_verified,   label: "Email verified" },
    { ok: user?.mobile_verified,  label: "Mobile verified" },
    { ok: user?.totp_enabled,     label: "2FA enabled" },
    { ok: !!phishCode,            label: "Anti-phishing code set" },
    { ok: user?.kyc_status === "approved", label: "KYC verified" },
    { ok: user?.safe_session,     label: "Safe session on" },
  ];
  const done = checks.filter((c) => c.ok).length;
  const score = Math.round((done / checks.length) * 100);
  const tone = score >= 80 ? "from-emerald-500 to-cm-blue"
             : score >= 50 ? "from-amber-500 to-cm-blue"
             : "from-red-500 to-cm-blue";
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11.5px] uppercase tracking-wider font-semibold text-muted-foreground">
            Security Score
          </div>
          <div className="text-[28px] font-semibold text-foreground mt-0.5">
            {score}<span className="text-muted-foreground text-[16px]"> / 100</span>
          </div>
          <p className="text-[12.5px] text-muted-foreground mt-0.5">
            {done} of {checks.length} security features active.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 max-w-[55%]">
          {checks.map((c) => (
            <span
              key={c.label}
              className={
                "px-2 py-0.5 rounded-full text-[10.5px] font-semibold border " +
                (c.ok
                  ? "border-up/30 text-up bg-up/10"
                  : "border-border text-muted-foreground bg-surface-2")
              }
            >
              {c.ok ? "✓ " : "○ "}{c.label}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-4 h-1.5 rounded-full bg-surface-2 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${tone} transition-all`}
             style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

/* ============================================================ */

const TONE = {
  ok:      "text-up bg-up/10 border-up/30",
  warn:    "text-warning  bg-warning/10  border-warning/30",
  err:     "text-down    bg-down/10    border-down/30",
  neutral: "text-muted-foreground    bg-surface-2  border-border",
};

function Tile({ icon: Icon, title, badge, tone = "neutral", description, cta, onClick, href, danger, testid }) {
  const inner = (
    <div
      onClick={onClick}
      className={
        "rounded-xl border bg-card p-4 hover:border-primary/60 hover:shadow-sm transition cursor-pointer h-full " +
        (danger ? "border-down/30" : "border-border")
      }
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={
          "w-9 h-9 rounded-lg grid place-items-center " +
          (danger
            ? "bg-down/10 text-down"
            : tone === "ok"   ? "bg-up/10 text-up"
            : tone === "warn" ? "bg-warning/10 text-warning"
            : tone === "err"  ? "bg-down/10 text-down"
            : "bg-surface-2 text-primary")
        }>
          <Icon size={17} />
        </div>
        {badge && (
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${TONE[tone]}`}>
            {badge}
          </span>
        )}
      </div>
      <div className="mt-3">
        <div className={`text-[14.5px] font-semibold ${danger ? "text-down" : "text-foreground"}`}>{title}</div>
        <p className="text-[12.5px] text-muted-foreground mt-1 leading-relaxed min-h-[34px]">
          {description}
        </p>
      </div>
      <div className={
        "mt-3 inline-flex items-center gap-1 text-[12px] font-semibold " +
        (danger ? "text-down" : "text-primary")
      }>
        {cta} <ChevronRight size={12} />
      </div>
    </div>
  );
  return href ? <Link to={href} className="block">{inner}</Link> : inner;
}

/* ============================================================ */

function Drawer({ title, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={
          "h-full bg-card border-l border-border overflow-y-auto " +
          (wide ? "w-full max-w-[640px]" : "w-full max-w-[480px]")
        }
      >
        <div className="sticky top-0 z-10 bg-card px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/* ============================================================ */

export function TwoFactorPanel({ user, onChanged }) {
  const enabled = !!user?.totp_enabled;
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableMode, setDisableMode] = useState(false);

  const start = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/api/auth/2fa/setup");
      setSetupData(data);
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    if (code.length !== 6) { setErr("Enter the 6-digit code."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/api/auth/2fa/verify", { code });
      setBackupCodes(data.backup_codes || []);
      setSetupData(null); setCode("");
      await onChanged?.();
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    if (code.length !== 6) { setErr("Enter your current 6-digit code."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/api/auth/2fa/disable", { code });
      setDisableMode(false); setCode("");
      await onChanged?.();
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (backupCodes) return (
    <div className="rounded-lg bg-warning/10 border border-warning/30 p-4 space-y-2">
      <div className="text-[12.5px] font-semibold text-warning inline-flex items-center gap-1.5">
        <AlertTriangle size={13} /> Save these backup codes
      </div>
      <p className="text-[12px] text-warning">
        Each code can be used once if you lose access to your authenticator. You won't see them again.
      </p>
      <div className="grid grid-cols-2 gap-1.5 font-mono text-[12.5px] bg-card p-2 rounded border border-warning/30">
        {backupCodes.map((c, i) => <div key={i}>{c}</div>)}
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={() => navigator.clipboard?.writeText(backupCodes.join("\n"))}
                className="px-3 py-1.5 rounded-md border border-warning/30 text-[12px] font-semibold text-warning inline-flex items-center gap-1.5">
          <Copy size={11} /> Copy all
        </button>
        <button onClick={() => setBackupCodes(null)}
                className="px-3 py-1.5 rounded-md brand-gradient text-primary-foreground text-[12px] font-semibold">
          I've saved them
        </button>
      </div>
    </div>
  );

  if (!enabled && !setupData) return (
    <div className="space-y-3">
      <p className="text-[13px] text-muted-foreground">
        Use Google Authenticator, Authy or 1Password to generate a 6-digit code at every sign-in.
      </p>
      <button
        onClick={start}
        disabled={busy}
        className="w-full px-4 py-2.5 rounded-lg brand-gradient text-primary-foreground font-semibold text-[13px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
        data-testid="settings-2fa-enable"
      >
        {busy && <Loader2 size={13} className="animate-spin" />}
        Enable 2FA
      </button>
    </div>
  );

  if (!enabled && setupData) return (
    <div className="space-y-3">
      <div className="rounded-lg bg-surface-2 border border-border p-3 space-y-3">
        <div className="text-[11.5px] uppercase tracking-wider font-semibold text-muted-foreground">
          1. Scan the QR with your authenticator app
        </div>
        <div className="flex items-start gap-3">
          {setupData.otpauth_uri && (
            <div className="shrink-0 bg-card p-2 rounded-md border border-border">
              <QRCodeSVG value={setupData.otpauth_uri} size={120} bgColor="#ffffff" fgColor="#0a131c" />
            </div>
          )}
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="text-[11px] text-muted-foreground">Or paste this secret manually:</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-2 py-1.5 rounded bg-card border border-border font-mono text-[11.5px] break-all">
                {setupData.secret}
              </code>
              <button onClick={() => navigator.clipboard?.writeText(setupData.secret)}
                      className="p-1.5 rounded border border-border hover:bg-card" title="Copy">
                <Copy size={12} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-[11.5px] uppercase tracking-wider font-semibold text-muted-foreground">
          2. Enter the 6-digit code from your app
        </label>
        <input inputMode="numeric" maxLength={6} value={code}
               onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
               placeholder="123456"
               className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary font-mono tracking-widest text-center"
               data-testid="settings-2fa-verify-input" />
      </div>
      {err && <div className="text-[12px] text-down">{err}</div>}
      <button onClick={verify} disabled={busy || code.length !== 6}
              className="w-full px-4 py-2.5 rounded-lg brand-gradient text-primary-foreground font-semibold text-[13px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="settings-2fa-verify">
        {busy && <Loader2 size={13} className="animate-spin" />}
        Verify & enable
      </button>
    </div>
  );

  // enabled
  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-up/10 border border-up/30 p-3 text-[12.5px] text-up inline-flex items-center gap-2">
        <ShieldCheck size={14} /> 2FA is enabled. You'll be prompted for a code at every sign-in.
      </div>
      {!disableMode ? (
        <button onClick={() => setDisableMode(true)}
                className="px-4 py-2.5 rounded-lg border border-down/30 text-down font-semibold text-[13px] hover:bg-down/10"
                data-testid="settings-2fa-disable">
          Disable 2FA
        </button>
      ) : (
        <div className="space-y-2 pt-2 border-t border-border">
          <div className="text-[12.5px] text-muted-foreground">
            Confirm with your current 6-digit code to disable.
          </div>
          <input inputMode="numeric" maxLength={6} value={code}
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                 placeholder="Current 6-digit code"
                 className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary font-mono tracking-widest text-center"
                 data-testid="settings-2fa-disable-input" />
          {err && <div className="text-[12px] text-down">{err}</div>}
          <div className="flex gap-2">
            <button onClick={disable} disabled={busy || code.length !== 6}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-down/90 text-white font-semibold text-[13px] disabled:opacity-50"
                    data-testid="settings-2fa-disable-confirm">
              Disable 2FA
            </button>
            <button onClick={() => { setDisableMode(false); setCode(""); setErr(""); }}
                    className="px-4 py-2.5 rounded-lg border border-border text-foreground font-semibold text-[13px]">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================ */

export function AccountActivityPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { setData((await api.get("/api/auth/account-activity")).data); }
    catch (e) { setMsg(getErrorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  const logoutOthers = async () => {
    if (!window.confirm("Log out all other sessions?")) return;
    setBusy(true); setMsg("");
    try {
      const { data: r } = await api.post("/api/auth/sessions/logout-others");
      setMsg(`Logged out ${r.removed} session(s).`);
      load();
    } catch (e) { setMsg(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
  const ua = (s) => {
    if (!s) return "Unknown device";
    const m = (s.match(/(Chrome|Firefox|Safari|Edge)/) || [])[0] || "Browser";
    const o = (s.match(/(Windows|Macintosh|iPhone|iPad|Android|Linux)/) || [])[0] || "Unknown OS";
    return `${m} on ${o}`;
  };

  if (!data) return <div className="text-muted-foreground text-[12.5px] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-5">
      <section>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className="text-[12px] uppercase tracking-wider font-semibold text-muted-foreground">Active sessions ({data.sessions?.length || 0})</h4>
          {(data.sessions?.length || 0) > 1 && (
            <button onClick={logoutOthers} disabled={busy}
                    className="px-2.5 py-1 rounded-md border border-down/30 text-down text-[11.5px] font-semibold hover:bg-down/10 disabled:opacity-50"
                    data-testid="logout-others-btn">
              Logout other sessions
            </button>
          )}
        </div>
        <div className="space-y-1.5" data-testid="sessions-list">
          {(data.sessions || []).length === 0 ? (
            <div className="text-muted-foreground text-[12px] italic">No active sessions tracked.</div>
          ) : data.sessions.map((s) => (
            <div key={s.id} className={
              "rounded-lg border p-3 text-[12.5px] " +
              (s.is_current ? "border-primary bg-primary/5" : "border-border bg-card")
            }>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-foreground">{ua(s.user_agent)}</span>
                {s.is_current && <span className="text-[10px] px-1.5 py-0.5 rounded-full brand-gradient text-primary-foreground font-semibold uppercase tracking-wider">This device</span>}
              </div>
              <div className="text-[11.5px] text-muted-foreground mt-0.5">
                {s.ip || "Unknown IP"} · Started {fmt(s.created_at)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h4 className="text-[12px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Recent activity</h4>
        <div className="rounded-lg border border-border divide-y divide-border overflow-hidden" data-testid="activity-list">
          {(data.activity || []).length === 0 ? (
            <div className="p-4 text-muted-foreground text-[12px] italic">No activity recorded.</div>
          ) : data.activity.map((a, i) => (
            <div key={i} className="px-3 py-2.5 flex items-center justify-between gap-3 text-[12.5px] hover:bg-surface-2 transition">
              <div className="min-w-0">
                <div className={"font-semibold truncate " + (a.success ? "text-foreground" : "text-down")}>
                  {(a.action || "").replace(/_/g, " ")}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {a.ip || "—"} · {ua(a.user_agent)}
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground whitespace-nowrap">{fmt(a.at)}</div>
            </div>
          ))}
        </div>
      </section>

      {msg && <div className="text-[12px] text-muted-foreground">{msg}</div>}
    </div>
  );
}

/* ============================================================ */

export function PasswordPanel({ onSuccess }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", totp_code: "" });
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr(""); setBusy(true);
    try {
      await api.post("/api/user/password", form);
      setMsg("Password updated. You'll be signed out in a moment.");
      onSuccess?.();
    } catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="password-panel">
      <input type="password" required placeholder="Current password"
             value={form.current_password}
             onChange={(e) => setForm({ ...form, current_password: e.target.value })}
             className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary outline-none text-[14px]"
             data-testid="pw-current" />
      <input type="password" required placeholder="New password (min 8 with Aa1!)"
             value={form.new_password}
             onChange={(e) => setForm({ ...form, new_password: e.target.value })}
             className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary outline-none text-[14px]"
             data-testid="pw-new" />
      <input inputMode="numeric" placeholder="2FA code (if enabled)"
             maxLength={6} value={form.totp_code}
             onChange={(e) => setForm({ ...form, totp_code: e.target.value.replace(/\D/g, "") })}
             className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary outline-none text-[14px] font-mono tracking-widest text-center"
             data-testid="pw-2fa" />
      {msg && <div className="text-[12px] text-up">{msg}</div>}
      {err && <div className="text-[12px] text-down">{err}</div>}
      <button type="submit" disabled={busy}
              className="w-full px-4 py-2.5 rounded-lg brand-gradient text-primary-foreground font-semibold text-[13px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="pw-submit">
        {busy && <Loader2 size={13} className="animate-spin" />}
        Update password
      </button>
    </form>
  );
}

/* ============================================================ */

export function DeleteAccountPanel({ user, onChanged, onClose }) {
  const isPending = user?.status === "pending_deletion";
  const [code, setCode] = useState(""); const [reason, setReason] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const codeLen = Number.isInteger(sent?.otp_length) && sent.otp_length >= 4 ? sent.otp_length : 6;

  const sendCode = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/api/auth/account/delete/send-otp");
      setSent(data);
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (code.length < codeLen) { setErr(`Enter the ${codeLen}-digit code we sent you.`); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/api/auth/account/delete", { code, reason });
      await onChanged?.();
      onClose?.();
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/api/auth/account/cancel-deletion");
      await onChanged?.();
      onClose?.();
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (isPending) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg bg-down/10 border border-down/30 p-3 text-[12.5px] text-down inline-flex items-start gap-2">
          <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
          <span>Your account is scheduled for deletion. Cancel anytime within 30 days to keep it.</span>
        </div>
        <button onClick={cancel} disabled={busy}
                className="w-full px-4 py-2.5 rounded-lg brand-gradient text-primary-foreground font-semibold text-[13px] disabled:opacity-50">
          Cancel account deletion
        </button>
        {err && <div className="text-[12px] text-down">{err}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-down/10 border border-down/30 p-3 text-[12.5px] text-down space-y-1">
        <div className="font-semibold inline-flex items-center gap-1.5"><AlertTriangle size={13} /> Read this carefully</div>
        <ul className="list-disc pl-4 space-y-0.5 opacity-90">
          <li>Account is scheduled for deletion 30 days from now.</li>
          <li>You can cancel anytime within those 30 days by signing in.</li>
          <li>All open orders and ads will be cancelled.</li>
          <li>You must withdraw any wallet balance first.</li>
        </ul>
      </div>
      <textarea rows={2} maxLength={300}
             placeholder="Why are you leaving? (optional)"
             value={reason} onChange={(e) => setReason(e.target.value)}
             className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary text-[13px]"
             data-testid="del-reason" />
      {!sent ? (
        <button onClick={sendCode} disabled={busy}
                className="w-full px-4 py-2.5 rounded-lg border border-border text-[13px] font-semibold transition hover:border-primary disabled:opacity-50"
                data-testid="del-send-otp">
          {busy ? "Sending code…" : "Send confirmation code"}
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-[12px] text-muted-foreground">
            Code sent to <span className="text-foreground">{sent.target}</span>.{" "}
            {sent.channel === "email" && !sent.dev_otp ? <>Also check your <strong>spam folder</strong>. </> : null}
            <button type="button" onClick={sendCode} disabled={busy} className="text-primary hover:underline">Resend</button>
          </p>
          {sent.dev_otp && <p className="text-[11px] text-muted-foreground">Dev code: <span className="font-mono">{sent.dev_otp}</span></p>}
          <input inputMode="numeric" maxLength={codeLen} placeholder={`${codeLen}-digit code`}
                 value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                 className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary font-mono tracking-widest text-center"
                 data-testid="del-otp" />
        </div>
      )}
      {err && <div className="text-[12px] text-down">{err}</div>}
      <button onClick={submit} disabled={busy || !sent || code.length < codeLen}
              className="w-full px-4 py-2.5 rounded-lg bg-down/90 text-white font-semibold text-[13px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="del-submit">
        {busy && <Loader2 size={13} className="animate-spin" />}
        Request account deletion
      </button>
    </div>
  );
}

/* ============================================================ */

export function AntiPhishingPanel({ current, onSaved }) {
  const [code, setCode] = useState(current || "");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const submit = async () => {
    if (code.length < 4) { setErr("Code must be at least 4 characters."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/api/auth/anti-phishing-code", { code });
      onSaved?.(data.anti_phishing_code);
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-muted-foreground">
        Choose a personal code (4-24 chars). Every official email we send you will include
        this code, so you can spot fake ones instantly.
      </p>
      <input value={code} onChange={(e) => setCode(e.target.value)}
             maxLength={24} placeholder="e.g. zebra-bahamas-37"
             className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary font-mono"
             data-testid="anti-phish-input" />
      {err && <div className="text-[12px] text-down">{err}</div>}
      <button onClick={submit} disabled={busy || !code.trim()}
              className="w-full px-4 py-2.5 rounded-lg brand-gradient text-primary-foreground font-semibold text-[13px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="anti-phish-submit">
        {busy && <Loader2 size={13} className="animate-spin" />}
        Save code
      </button>
    </div>
  );
}
